<template>
  <div class="reporting__stream">
    <h3 v-if="!isCta">
      <span v-if="!isDesktopDevice" class="title"><i class="icon icon--logo-mobile"></i><i class="icon icon--logo-mobile-path"></i> {{ $t('reporting.stream.titleOnly') }}</span>
      <span v-else class="title" v-html="$t('reporting.stream.title')"></span>
    </h3>

    <!-- Activity Statistics chart -->
    <div v-if="!isCta" class="reporting__stream__statistics">
      <div class="reporting__stream__statistics-title">
        <span class="title" v-html="$t('reporting.stream.activityStatistics.title')"></span>
      </div>
      <div class="reporting__stream__statistics-graph">
        <div class="lines">
          <div class="vertical">
            <span></span><span></span><span></span>
          </div>
          <div class="horizontal">
            <span></span><span></span><span></span>
          </div>
        </div>
        <div class="line nothing"></div>
        <div class="line median"
             v-bind:class="{ 'active': activityStatistics === 'median' }"
             @click="setActivityStatistics('median')">
          <span class="label" v-html="$t('reporting.stream.activityStatistics.medianGroup.label')"></span>
        </div>
        <div class="line extreme"
             v-bind:class="{ 'active': activityStatistics === 'extreme' }"
             @click="setActivityStatistics('extreme')">
          <span class="label" v-html="$t('reporting.stream.activityStatistics.extremeGroup.label')"></span>
        </div>
      </div>
      <div class="reporting__stream__statistics-label">
        <div class="label median" v-if="activityStatistics === 'median'"><span v-html="$t('reporting.stream.activityStatistics.medianGroup.description')"></span></div>
        <div class="label extreme" v-if="activityStatistics === 'extreme'"><span v-html="$t('reporting.stream.activityStatistics.extremeGroup.description')"></span></div>
      </div>
      <div class="reporting__stream__statistics-description">
        <div class="wrap">
          <div>
            <i class="icon icon--info-outline-size17"></i>
            <span v-if="activityStatistics === 'median'" v-html="$t('reporting.stream.activityStatistics.medianGroup.information')"></span>
            <span v-if="activityStatistics === 'extreme'" v-html="$t('reporting.stream.activityStatistics.extremeGroup.information')"></span>
          </div>
        </div>
      </div>
    </div>

    <!-- conversion selecting dial -->
    <div v-if="!isCta" class="reporting__stream__dial">
      <div class="dial">
        <div class="reporting__stream__dial-needle"
             v-bind:class="[
              conversionDial,
              isConversionDialOnNext ? 'next' : ''
            ]"
             @click="setConversionDial('next')"></div>
        <div class="reporting__stream__dial-line">
          <div class="text"
               v-bind:class="{ active: ['top', 'top cameback'].includes(conversionDial) }"
               @click="setConversionDial('top')">
            {{ $t('reporting.stream.conversion.allLabel') }}
          </div>
          <div class="text"
               v-bind:class="{ active: conversionDial === 'middle' }"
               @click="setConversionDial('middle')">
            {{ $t('reporting.stream.conversion.conversionLabel') }}
          </div>
          <div class="text"
               v-bind:class="{ active: conversionDial === 'bottom' }"
               @click="setConversionDial('bottom')">
            {{ $t('reporting.stream.conversion.nonConversionLabel') }}
          </div>
        </div>
      </div>
    </div>

    <!-- no data -->
    <div v-if="!hasData" class="no-data">
      <p v-html="$t('reporting.stream.noStreamData')"></p>
    </div>

    <div v-else>

      <!-- stream -->
      <div class="reporting__stream__history content-box">
        <div>
          <div class="reporting__stream__history-title">
            <span class="title" v-html="$t('reporting.stream.navigationFlow.title')"></span>
          </div>
          <div v-if="hasNoCoordsElement"
            class="no-coords-info"
            v-html="$t('reporting.stream.hasNoCoordsElement')"></div>
          <table ref="activityTable">
            <caption>Activity History Table</caption>
            <colgroup>
              <col width="18%">
              <col width="30%">
              <col width="25%">
              <col width="27%">
            </colgroup>
            <thead>
            <tr>
              <th>{{ $t('common.table.header.sequence') }}</th>
              <th>{{ $t('common.table.header.content') }}</th>
              <th>{{ $t('common.table.header.typeAction') }}</th>
              <th>{{ $t('common.table.header.duration') }}</th>
            </tr>
            </thead>
            <tbody>
              <tr v-if="hasInvalidData">
                <td class="no-available-node" colspan="4">
                  {{ $t('reporting.stream.noAvailableNode') }}
                </td>
              </tr>
              <template v-else>
                <tr v-for="(item, index) in pathItems" :key="index"
                  :class="[pathCssClasseList[index], item.duration == longestDuration ? 'longest' : '']"
                  @mouseenter="setHoverIndex(index)"
                  @mouseleave="setHoverIndex(null)"
                  @click="setActiveIndex(index)">
                  <td class="data-sequence">
                    <div class="sequence">
                      <div v-if="actionData.activeIndex === index"
                        class="active-mask"
                        :style="{ width: tableWidth + 'px' }"></div>
                      <i>
                        <span>{{ index + 1 }}</span>
                      </i>
                    </div>
                  </td>
                  <td class="data-element">
                    <figure>
                      <span v-if="item.isOffScreen" class="empty-element">
                        <i class="icon icon--empty-item"
                          @mouseenter="emptyElementHoverIndex = index"
                          @mouseleave="emptyElementHoverIndex = null"></i>
                        <ui-icon-tooltip class="empty-tooltip"
                          :forceShowText="emptyElementHoverIndex === index"
                          iconSize="normal"
                          arrowRight>
                          {{ $t('reporting.stream.outOfArea') }}
                        </ui-icon-tooltip>
                      </span>
                      <crop-image v-else
                        :url="imageData.url"
                        :imageWidth="imageData.width"
                        :imageHeight="imageData.height"
                        :width="item.coords.width"
                        :height="item.coords.height"
                        :maxWidth="80"
                        :maxHeight="40"
                        :offset="{ x: item.coords.left, y: item.coords.top }"/>
                    </figure>
                  </td>
                  <td class="data-type">
                    <span class="type">{{ item.r[6] | streamElementType }}</span>
                    <span class="delimiter"><span>&nbsp;</span>/&nbsp;</span>
                    <span class="event">{{ item.event | streamEventType }}</span>
                  </td>
                  <td class="data-duration">
                    <span v-html="getDurationStr(item.duration)"></span>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
      <slot name="footer"/>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getTimeParts } from '@/libs/utils'
import CropImage from '@/components/ui/CropImage'
import UiIconTooltip from '@/components/ui/UiIconTooltip'

export default {
  name: 'ReportStream',
  components: {
    CropImage,
    UiIconTooltip
  },
  props: [
    'isLoading',
    'isCta',
    'reportName',
    'reportData',
    'activeSelector',
    'userActionData'
  ],
  data () {
    return {
      tableWidth: 0,
      emptyElementHoverIndex: null,
      conversionDial: 'top',
      isConversionDialMoving: false,
      isConversionDialOnNext: false,
      activityStatistics: 'median',
      longestDuration: 0
    }
  },
  computed: {
    ...mapState([
      'resizeTimestamp'
    ]),
    isDesktopDevice () {
      return this.reportData && this.reportData.device === 'desktop'
    },
    pathItems () {
      if (this.reportData.data && this.reportData.data.path) {
        this.setLongestDuration()
        return this.reportData.data.path
      }
      return []
    },
    pathCssClasseList () {
      const items = []
      const hoverIndex = this.actionData.hoverIndex
      const activeIndex = this.actionData.activeIndex

      for (let i = 0; i < this.pathItems.length; i++) {
        const cssClass = {
          hover: hoverIndex === i,
          active: activeIndex === i,
          'active-prev': activeIndex === i + 1,
          'active-next': activeIndex === i - 1,
          'off-screen': this.pathItems[i].isOffScreen
        }

        items.push(cssClass)
      }
      return items
    },
    hasNoCoordsElement () {
      return this.reportData.data && this.reportData.data.hasNoCoordsItem
    },
    hasData () {
      return this.reportData && this.reportData.data
    },
    hasInvalidData () {
      return this.pathItems && !this.pathItems.length
    },
    imageData () {
      return this.reportData.image
    },
    actionData () {
      if (this.userActionData) {
        return this.userActionData
      }

      return {
        hoverIndex: null,
        activeIndex: null
      }
    }
  },
  watch: {
    resizeTimestamp: {
      handler (value) {
        this.setTableWidth()
      }
    }
  },
  created () {
  },
  mounted () {
    this.setTableWidth()
  },
  methods: {
    setLongestDuration () {
      this.longestDuration = 0
      if (this.reportData.data && this.reportData.data.path) {
        for (let i = 0; i < this.reportData.data.path.length; i++) {
          if (this.longestDuration <= this.reportData.data.path[i].duration) {
            this.longestDuration = this.reportData.data.path[i].duration
          }
        }
      }
    },
    setTableWidth () {
      const el = this.$refs.activityTable

      if (el) {
        this.tableWidth = el.offsetWidth
      }
    },
    setHoverIndex (index) {
      const data = {
        hoverIndex: index,
        activeIndex: this.actionData.activeIndex
      }
      this.$emit('onUserActionDataChange', data)
    },
    setActiveIndex (index) {
      this.setTableWidth()

      if (this.actionData.activeIndex === index) {
        index = null
      }

      const data = {
        hoverIndex: this.actionData.hoverIndex,
        activeIndex: index
      }
      this.$emit('onUserActionDataChange', data)
    },
    setActivityStatistics (target) {
      this.activityStatistics = target
      this.actionData.activeIndex = null
      this.$emit('onActivityStatisticsChange', target)
    },
    setConversionDial (position, next = false) {
      if (position === 'next' && !this.isConversionDialMoving) {
        this.isConversionDialOnNext = true
        if (['top', 'top cameback'].includes(this.conversionDial)) {
          return this.setConversionDial('middle', true)
        } else if (this.conversionDial === 'middle') {
          return this.setConversionDial('bottom', true)
        } else if (this.conversionDial === 'bottom') {
          return this.setConversionDial('top cameback', true)
        }
      } else if (position !== 'next') {
        this.isConversionDialOnNext = next
        this.isConversionDialMoving = true
        let self = this
        setTimeout(function () {
          self.isConversionDialMoving = false
          if (self.conversionDial === 'top cameback') {
            self.conversionDial = 'top'
          }
        }, 500)

        this.conversionDial = position
        let value = null
        if (position === 'middle') {
          value = true
        } else if (position === 'bottom') {
          value = false
        }
        this.actionData.activeIndex = null
        this.$emit('onConversionChange', value)
      }
    },
    getDurationStr (ms) {
      const timeParts = getTimeParts(ms, 1)

      let hours = timeParts.h
      const minutes = timeParts.m
      const seconds = timeParts.s

      if (timeParts.d) {
        hours += timeParts.d * 24
      }

      let str = `${seconds}s`

      if (minutes) {
        str = `${minutes}m ${str}`
      }

      if (hours) {
        str = `<span class="hours">${hours}h</span> ${str}`
      }
      return str
    }
  }
}
</script>

<style lang="scss">
.reporting__stream {
  font-size: 13px;
  position: relative;

  h3 {
    font-size: 22px;
    font-weight: 500;
    color: #444444;

    img, svg {
      vertical-align: bottom;
    }
    i {
      vertical-align: middle;
      margin-right: 3px;
    }
  }

  .icon--logo-mobile-path {
    margin-left: 10px;
  }

  .reporting__stream__statistics {
    border-radius: 14px;
    box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
    background-color: #1b1b1d;
    margin: 20px 0 30px 0;
    word-break: keep-all;

    .reporting__stream__statistics-title {
      font-size: 15px;
      color: #fff;
      padding: 25px 25px 30px 25px;
    }

    .reporting__stream__statistics-graph {
      position: relative;
      height: 116px;
      margin: 0 34px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.5);
      background-size: 100% 100%;

      .lines {
        position: absolute;
        width: 100%;
        height: 100%;

        .vertical {
          span {
            position: absolute;
            top: 0;
            left: 25%;
            height: 100%;
            border-left: 1px solid rgba(171, 133, 255, 0.5);
          }

          span:nth-child(2) {
            left: 50%;
          }

          span:nth-child(3) {
            left: 75%;
          }
        }

        .horizontal {
          span {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            border-top: 1px solid rgba(255, 255, 255, 0.12);
          }

          span:nth-child(2) {
            top: 33.33333%;
          }

          span:nth-child(3) {
            top: 66.66666%;
          }
        }
      }

      &::before {
        content: "Activity";
        position: absolute;
        bottom: -20px;
        right: 0;
        color: #767676;
        font-size: 12px;
      }

      &::after {
        content: "User";
        position: absolute;
        top: 5px;
        left: -25px;
        color: #767676;
        font-size: 12px;
        transform: rotate(-90deg);
      }

      .line {
        &.nothing {
          position: absolute;
          top: 0;
          left: 0;
          width: 25%;
          height: 100%;
          background: url(../../assets/img/heatmap/img-line-curve-01.svg) no-repeat center top;
          background-size: 105% 102.5%;
        }

        &.median {
          position: absolute;
          top: 0;
          left: 25%;
          width: 50%;
          height: 100%;
          background: url(../../assets/img/heatmap/img-line-curve-02.svg) rgba(171, 133, 255, 0.5) no-repeat center top;
          background-size: 105% 102.5%;
          transition: background-color 0.15s ease;
          cursor: pointer;

          &.active {
            box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
            border: 3px solid #ffffff;
            border-radius: 7px;
            margin: -3px;
            z-index: 10;

            &:before {
              content: "";
              position: absolute;
              bottom: -8px;
              left: 50%;
              margin-left: -6px;
              width: 0;
              height: 0;
              border-left: 6px solid transparent;
              border-right: 6px solid transparent;
              border-top: 6px solid #fff;
            }
          }

          &:not(.active):hover {
            background-color: rgba(204, 172, 255, 0.75);
          }
        }

        &.extreme {
          position: absolute;
          top: 0;
          left: 75%;
          width: 25%;
          height: 100%;
          background: url(../../assets/img/heatmap/img-line-curve-03.svg) rgba(99, 61, 182, 0.5) no-repeat center top;
          background-size: 105% 102.5%;
          transition: background-color 0.15s ease;
          cursor: pointer;

          &.active {
            box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
            border: 3px solid #ffffff;
            border-radius: 7px;
            margin: -3px;
            z-index: 10;

            &:before {
              content: "";
              position: absolute;
              bottom: -8px;
              left: 20%;
              margin-left: -6px;
              width: 0;
              height: 0;
              border-left: 6px solid transparent;
              border-right: 6px solid transparent;
              border-top: 6px solid #fff;
            }
          }

          &:not(.active):hover {
            background-color: rgba(194, 160, 255, 0.75);
          }
        }

        .label {
          display: block;
          font-size: 14px;
          padding: 26px 10px 45px 10px;
          height: 27px;
          background: url(../../assets/img/heatmap/img-path-check-02.svg) no-repeat center bottom;
          color: #dfdfdf;
          line-height: 1.15;
          text-align: center;
        }

        &:hover .label {
          background-image: url(../../assets/img/heatmap/img-path-check-03.svg);
        }

        &.active .label {
          background-image: url(../../assets/img/heatmap/img-path-check-01.svg);
        }
      }
    }

    .reporting__stream__statistics-label {
      display: table;
      height: 65px;
      width: calc(100% - 65px);
      margin: 5px auto 0 auto;
      line-height: 1.5;
      font-size: 12px;

      .label {
        display: table-cell;
        vertical-align: middle;

        &.median {
          text-align: center;
          color: #fbcf0a;

          span {
            display: inline-block;
            max-width: 65%;
            margin: 0 auto;
          }
        }

        &.extreme {
          display: table-cell;
          vertical-align: middle;
          text-align: right;
          color: #fb810a;

          span {
            br {
                display: none;
                @media screen and (max-width: 1500px) {
                  display: block;
                }
              }
          }
        }
      }
    }

    .reporting__stream__statistics-description {
      border-top: 1px solid #444444;
      color: #8d8d8d;
      line-height: 1.43;
      display: table;
      width: 100%;
      height: 110px;

      .wrap {
        display: table-cell;
        vertical-align: middle;

        div {
          position: relative;
          padding:11px 26px 16px 45px;
          max-width: 500px;
          margin: 0 auto;

          .icon--info-outline-size17 {
            position: relative;
            top: 4px;
            left: -23px;
            margin-right: -17px;
          }
        }
      }
    }
  }

  .reporting__stream__dial {

    .dial {
      position: relative;
      height: 112px;
      background:url(../../assets/img/heatmap/heatmap_dial.svg) no-repeat;
      margin: 27px 0 -2px 15px;

      .reporting__stream__dial-needle {
        position: absolute;;
        top: 9px;
        left: 12px;
        width: 88px;
        height: 88px;
        cursor: pointer;
        background:url(../../assets/img/heatmap/heatmap_dial_needle.svg) no-repeat;
        transform: rotate(0deg);
        transition: transform 0.5s ease;

        &.top {
          transform: rotate(64deg);
          &.next {
            transition-duration: 0s;
          }
          &.cameback {
            transform: rotate(424deg);
            transition-duration: 0.5s;
          }
        }

        &.middle {
          transform: rotate(90deg);
        }

        &.bottom {
          transform: rotate(116deg);
        }
      }

      .reporting__stream__dial-line {
        position: absolute;
        top: 0;
        left: 120px;
        width: calc(100% - 120px);
        height: 88px;
        padding: 9px 12px;

        &::after,
        &::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 20px;
          height: 106px;
          background:url(../../assets/img/heatmap/heatmap_dial_line.svg) no-repeat left center;
        }

        &::after {
          left: 20px;
          width: 10%;
          background-position: right center;
        }

        .text {
          white-space: nowrap;
          font-size: 14px;
          color: #aaaaaa;
          padding: 0 0 21px calc(10% + 20px);
          height: 16px;
          cursor: pointer;

          &.active {
            color: #555555;
          }
        }
      }
    }
  }

  .reporting__stream__history {
    margin-top: 30px;

    .reporting__stream__history-title .title {
      font-size: 15px;
      color: #2f2f2f;
      font-weight: bold;
    }

    .no-coords-info {
      padding-top: 16px;
      color: $brandColor;
      font-size: 12px;
    }

    table {
      margin-top: 30px;
      width: 100%;
      text-align: center;
      color: #2f2f2f;

      caption {
        visibility: hidden;
        width: 0;
        height: 0;
      }

      thead tr th {
        padding-bottom: 8px;
        border-bottom: 1px solid #bbbbbb;
        font-size: 12px;
        font-weight: normal;
        color: #444;
      }

      tbody tr {
        height: 59px;
        border-bottom: 1px dashed #d7d7d7;
        cursor: pointer;

        td {
          height: 59px;
          transition: background-color 0.15s ease;

          &.data-type {
            .type {
              display: block;
            }
            .delimiter span {
              display: none;
            }
          }

          &.data-duration {
            .hours {
              display: block;
            }
          }

          .empty-element {
            position: relative;
            display: inline-block;
            .empty-tooltip {
              pointer-events: none;
              position: absolute;
              right: -6px;
              top: -3px;
            }
          }

          &.no-available-node {
            padding-top: 130px;
            padding-bottom: 125px;
          }
        }

        .sequence {
          position: relative;
          width: 100%;
          height: 100%;

          &:before {
            content: '';
            width: 1px;
            height: 12px;
            border-left: 2px solid #d7d7d7;
            position: absolute;
            left: 50%;
            top: 0;
            transform: translateX(-50%);
          }

          &:after {
            content: '';
            width: 1px;
            height: 12px;
            border-left: 2px solid #d7d7d7;
            position: absolute;
            left: 50%;
            bottom: 0;
            transform: translateX(-50%);
          }

          i {
            @include positionXYcenter;
            width: 36px;
            height: 36px;
            box-sizing: border-box;
            border: solid 1px #c1c1c1;
            border-radius: 50%;
            background-color: #ffffff;
            z-index: 2;

            span {
              font-size: 14px;
              font-weight: bold;
              line-height: 1;
              color: $brandColor;
              @include positionXYcenter;
            }
          }
        }

        &.longest {
          td {
            color: #592cb8;
            font-weight: 500;

            &.data-duration {
              font-size: 20px;
            }

            .sequence {
              i {
                box-shadow: 0 5px 4px -2px rgba(0, 0, 0, 0.2);
                border: solid 1px rgba(0, 0, 0, 0.45);

                &::before {
                  content: "";
                  position: absolute;
                  top: -5px;
                  left: -5px;
                  width: 16px;
                  height: 16px;
                  background:url(../../assets/img/heatmap/img-badge-star.svg) no-repeat center center;
                }
              }
            }
          }
        }

        &.hover {
          td {
            background-color: rgb(240,240,240);
          }
        }

        &.active {
          cursor: default;
          td {
            background-color: transparent;
            transition: background-color linear 0s;

            span {
              position: relative;
              z-index: 2;
            }

            i span {
              position: absolute;
            }
          }

          .active-mask {
            position: absolute;
            top: -1px;
            left: -10px;
            z-index: 1;
            pointer-events: none;
            height: 57px;
            padding: 0 8px;
            border-radius: 10px;
            box-shadow: 0 12px 10px -4px rgba(0, 0, 0, 0.15);
            border: solid 2px $brandColor;
          }

          &.longest {
            .active-mask {
              background-color: rgb(237,229,255);
            }
          }

          .sequence {
            &:before {
              display: none;
            }
            &:after {
              display: none;
            }

            i {
              background-color: $brandColor;
              border-color: $brandColor;
              span {
                color: #fff !important;
              }
            }
          }
        }

        &.active-prev,
        &.active-next {
          .sequence i {
            border-color: $brandColor;
            span {
              color: $brandColor !important;
            }
          }
        }

        &.active-next {
          .sequence {
            &::before {
              border-left-color: #ffac00;
            }
          }
        }

        &.active-prev {
          .sequence {
            &::after {
              border-left-color: #ffac00;
            }
          }
        }

        &:nth-of-type(1) {
          .sequence {
            &:before {
              display: none !important;
            }
          }
        }
        &:nth-last-of-type(1) {
          border-bottom: none;
          .sequence {
            &:after {
              display: none !important;
            }
          }
        }
      }
    }
  }
}

@media screen and (min-width: 1790px) {
  .reporting__stream {
    td.data-type {
      .type {
        display: inline !important;
      }
      .delimiter span {
        display: inline !important;
      }
    }
    td.data-duration {
      .hours {
        display: inline !important;
      }
    }
  }
}
</style>
